/**
 * [Insider+] Shared lifecycle hook for the Insider+ iframe-hosting pages.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { decode } from 'jsonwebtoken';

import {
  issueSsoToken,
  getInsiderBenefits,
} from '../../request/insidersRequest';
import { selectAuthUser } from '../../store/selectors/userSelectors';
import { setInsiderBenefits } from '../../store/actions/user/userActions';
import { pushInsiderFunnelEventOnce } from '../helpers/insiderPlusEntryHelper';
import {
  getTargetOrigin,
  isIframeOriginAllowed,
  isSafeNavigationUrl,
} from '../../security/config';
import {
  registerIframeForLogout,
  sendApiBaseUrlToIframe,
  sendSsoTokenToIframe,
  setupIframeTokenListener,
  unregisterIframeForLogout,
} from '../iframeJwtUtils';

const MAX_AUTH_RETRIES = 2;

// Module-level SSO token cache shared across the Insider+ pages. Welcome and
// Membership each mount their own useInsiderPlusIframe, so navigating between
// them — or re-clicking the header — would otherwise re-mint a token via
// issue-sso-token on every mount. Caching lets a still-valid token be reused
// across navigations; combined with the child's identity-validated skip, a
// repeat visit triggers neither issue-sso-token nor validate-external-jwt.
//
// Safe at module scope because the app only mounts one Insider+ iframe at a
// time (see the matching note in iframeJwtUtils).
let cachedSsoToken = null;
let cachedSsoTokenExpMs = 0; // epoch ms; 0 = unknown expiry → never reused
// User the cached token was minted for. The cache is module-level and survives
// SPA navigations — including the logout redirect (history.replace), which does
// NOT reload the page. Without keying on the user, a logout→login as a different
// user could serve the previous user's token to the new one. We only reuse when
// the cached user matches the current user.
let cachedSsoTokenUserUid = null;

// Only reuse while the token has comfortable life left to survive the push to
// the iframe plus the child's exchange.
const SSO_TOKEN_REUSE_BUFFER_MS = 60 * 1000;

const isCachedSsoTokenUsable = (userUid) =>
  Boolean(cachedSsoToken) &&
  cachedSsoTokenUserUid === userUid &&
  cachedSsoTokenExpMs - Date.now() > SSO_TOKEN_REUSE_BUFFER_MS;

const cacheSsoToken = (token, userUid) => {
  cachedSsoToken = token;
  cachedSsoTokenUserUid = userUid ?? null;
  try {
    const exp = decode(token)?.exp;
    cachedSsoTokenExpMs = typeof exp === 'number' ? exp * 1000 : 0;
  } catch {
    cachedSsoTokenExpMs = 0;
  }
};

const clearCachedSsoToken = () => {
  cachedSsoToken = null;
  cachedSsoTokenExpMs = 0;
  cachedSsoTokenUserUid = null;
};

// After SUBSCRIPTION_SUCCESS: how many times, and how far apart, to ask the
// benefits API for the live status. The checkout is already written by then, so
// one call normally answers it; the retries cover a transient network error or
// a response that isn't active yet.
const BENEFITS_CONFIRM_MAX_ATTEMPTS = 3;
const BENEFITS_CONFIRM_RETRY_MS = 1000;

/**
 * Shared lifecycle for the Insider+ iframe-hosting pages.
 *
 * Centralizes SSO token issue + ack retry, AUTH_FAILED recovery, subscription
 * refresh, API base URL push, and re-seeding the iframe src on location.key
 * changes (header re-clicks while already on the page).
 *
 * @param {Object} options
 * @param {() => string} options.buildIframeSrc - returns the iframe src for
 *   the current state. Called on mount and again on every location.key change.
 *   Memoize with useCallback so the effect dep array stays stable.
 * @param {(url: string) => void} [options.onNavigateUrl] - called when the
 *   iframe sends NAVIGATE_TO_URL with a URL that has passed the origin +
 *   protocol checks. Need not be memoized — held via ref internally.
 * @param {(event: MessageEvent) => void} [options.onIframeMessage] - optional
 *   additional postMessage handler (origin already validated). Need not be
 *   memoized.
 * @param {React.MutableRefObject<string|null>} [options.skipNextLocationSyncRef] -
 *   optional ref a caller can set to the pathname it's about to
 *   history.replace/push to itself, in reaction to something the iframe
 *   already did (e.g. mirroring the child's IFRAME_ROUTE_CHANGED onto the
 *   visible parent URL). The child is already at that path — reacting to
 *   the resulting location.key change by recomputing iframeSrc and
 *   remounting the <iframe> would force it to reload for no reason. Tied to
 *   the specific pathname (rather than a bare boolean) so that if a second
 *   self-sync lands before the first one's effect run has fired, the first
 *   run isn't tricked into skipping for the wrong navigation. Consumed
 *   (reset to null) only when it matches the location the effect actually
 *   sees.
 */
const useInsiderPlusIframe = ({
  buildIframeSrc,
  onNavigateUrl,
  onIframeMessage,
  skipNextLocationSyncRef,
}) => {
  const dispatch = useDispatch();
  const user = useSelector(selectAuthUser);
  const location = useLocation();

  const iframeRef = useRef(null);
  const [ssoToken, setSsoToken] = useState(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [isRecovering, setIsRecovering] = useState(false);
  const [authFailed, setAuthFailed] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  // Synchronously initialize from buildIframeSrc so the first paint already
  // points at the right URL (matters for pages that derive src from storage).
  const [iframeSrc, setIframeSrc] = useState(buildIframeSrc);
  // iframeKey drives a React-level remount of the <iframe>. We bump it on
  // every location.key change so a header re-click while already on the
  // current page forces a full iframe reload even when buildIframeSrc
  // returns an unchanged URL — browsers don't reload an <iframe> when its
  // src is reassigned to the same value, so a state-only update wouldn't
  // be enough.
  const [iframeKey, setIframeKey] = useState(0);

  const retryCountRef = useRef(0);
  const isRecoveringRef = useRef(false);
  const isRetryingRef = useRef(false);
  const hasMountedRef = useRef(false);

  // Refs mirror dynamic values so the token-listener effect can register
  // exactly once. Re-registering on every state change races with in-flight
  // iframe requests: the previous listener's cleanup discards
  // pendingRequests in its closure, so any REQUEST_SSO_TOKEN that arrived
  // mid-recovery would be dropped.
  const ssoTokenRef = useRef(null);
  useEffect(() => {
    ssoTokenRef.current = ssoToken;
  }, [ssoToken]);

  const callbacksRef = useRef({ onNavigateUrl, onIframeMessage });
  useEffect(() => {
    callbacksRef.current = { onNavigateUrl, onIframeMessage };
  });

  // Mirror the authenticated user's id so fetchSsoToken (kept stable, []-deps)
  // can key the module-level token cache to the current user without being
  // re-created on every user change.
  const userUidRef = useRef(user?.UserUid ?? null);
  useEffect(() => {
    userUidRef.current = user?.UserUid ?? null;
  }, [user]);

  // Reuse a still-valid cached token unless `force` is set. Forcing is used on
  // auth-failure recovery and explicit retry, where the current token may be
  // the problem and a freshly minted one is required.
  const fetchSsoToken = useCallback(async (force = false) => {
    const userUid = userUidRef.current;
    if (!force && isCachedSsoTokenUsable(userUid)) {
      setSsoToken(cachedSsoToken);
      return true;
    }
    try {
      const response = await issueSsoToken();
      // Token can be at response.data.ssoToken OR directly at response.data
      const token = response?.data?.ssoToken || response?.data;
      if (token && typeof token === 'string') {
        cacheSsoToken(token, userUid);
        setSsoToken(token);
        return true;
      }
      return false;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[useInsiderPlusIframe] Error fetching SSO token:', error);
      return false;
    }
  }, []);

  useEffect(() => {
    fetchSsoToken();
  }, [fetchSsoToken]);

  // NAVIGATE_TO_URL + page-specific messages from the iframe. Registered once;
  // callbacks read through the ref so consumers don't have to memoize.
  // Non-NAVIGATE_TO_URL messages (e.g. IFRAME_ROUTE_CHANGED, NPS_ELIGIBLE) are
  // forwarded to the page's onIframeMessage callback.
  useEffect(() => {
    const handleMessage = (event) => {
      if (!isIframeOriginAllowed(event.origin)) return;
      if (event.data?.type === 'NAVIGATE_TO_URL' && event.data?.url) {
        if (
          isSafeNavigationUrl(event.data.url) &&
          callbacksRef.current.onNavigateUrl
        ) {
          callbacksRef.current.onNavigateUrl(event.data.url);
        }
        return;
      }
      if (callbacksRef.current.onIframeMessage) {
        callbacksRef.current.onIframeMessage(event);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Handlers stored in refs so the token-listener effect can stay
  // []-deps. Retry budget is gated on retryCountRef directly (rather than
  // just isRecoveringRef) so a burst of AUTH_FAILED events after recovery
  // resolves can't bypass MAX_AUTH_RETRIES.
  const handleAuthFailureRef = useRef(null);
  handleAuthFailureRef.current = async () => {
    if (isRecoveringRef.current) return;
    if (retryCountRef.current >= MAX_AUTH_RETRIES) {
      setAuthFailed(true);
      return;
    }
    retryCountRef.current += 1;
    isRecoveringRef.current = true;
    setIsRecovering(true);
    // The cached token may be what failed — drop it and mint a fresh one.
    clearCachedSsoToken();
    setSsoToken(null);
    try {
      await fetchSsoToken(true);
    } finally {
      isRecoveringRef.current = false;
      setIsRecovering(false);
    }
  };

  const handleSubscriptionSuccessRef = useRef(null);
  // The child's checkout call has already written the subscription by the time
  // it sends SUBSCRIPTION_SUCCESS, so the benefits API should now report it.
  // The gateway caches that endpoint (server and browser, 60s) though, and
  // useInitHook filled the cache with "not subscribed" at app load — so every
  // attempt carries a throwaway `_` param to skip both caches. Only an active
  // response is written to the store; that is what swaps the header's plain
  // Insider+ link for the dropdown, and a stale one can't knock the state back
  // to loading. Retries cover a network error or a not-yet-active response; a
  // 500 goes to the error page via internalServerErrorMiddleware, which
  // unmounts the page and so ends the poll. A new success message restarts the
  // poll, and unmounting cancels it so a late response can't land on another
  // user.
  const benefitsPollRef = useRef({ runId: 0, timerId: null });

  const stopBenefitsPoll = useCallback(() => {
    const poll = benefitsPollRef.current;
    poll.runId += 1;
    clearTimeout(poll.timerId);
    poll.timerId = null;
  }, []);

  useEffect(() => stopBenefitsPoll, [stopBenefitsPoll]);

  handleSubscriptionSuccessRef.current = () => {
    const userUid = user?.UserUid;
    if (!userUid) return;

    stopBenefitsPoll();
    const poll = benefitsPollRef.current;
    const { runId } = poll;

    const confirmSubscription = (attempt) => {
      const retryOrGiveUp = () => {
        if (attempt >= BENEFITS_CONFIRM_MAX_ATTEMPTS) {
          // eslint-disable-next-line no-console
          console.error(
            '[useInsiderPlusIframe] Benefits never reported an active subscription after checkout',
          );
          return;
        }
        poll.timerId = setTimeout(
          () => confirmSubscription(attempt + 1),
          BENEFITS_CONFIRM_RETRY_MS,
        );
      };

      getInsiderBenefits(userUid, { _: Date.now() })
        .then(({ data }) => {
          if (poll.runId !== runId) return;
          if (data?.HasActiveInsiderPlusSubscription === true) {
            dispatch(setInsiderBenefits(data));
            return;
          }
          retryOrGiveUp();
        })
        .catch(() => {
          if (poll.runId !== runId) return;
          retryOrGiveUp();
        });
    };

    confirmSubscription(1);
  };

  useEffect(() => {
    // Route REQUEST_SSO_TOKEN responses through sendSsoTokenToIframe so
    // child-initiated requests get the same nonce + generation guarantees
    // as parent-initiated pushes. Without this, the listener's inline
    // response could carry a stale token (ssoTokenRef may lag setSsoToken
    // by one effect tick) and clobber a freshly pushed value.
    //
    // Propagate sendSsoTokenToIframe's boolean — it returns false when the
    // iframe element is mounted but its contentWindow is null (e.g. during
    // the brief window between key-bump remount and the new iframe load).
    // Lying "sent" in that gap would let the listener drop the pending
    // request and miss the eventual delivery once the iframe is ready.
    const sendSsoToken = () => {
      if (!ssoTokenRef.current) return false;
      return sendSsoTokenToIframe(
        iframeRef.current,
        ssoTokenRef.current,
        getTargetOrigin(),
      );
    };

    const cleanup = setupIframeTokenListener(
      () => null, // No JWT token for these pages
      () => ssoTokenRef.current,
      () => handleAuthFailureRef.current?.(),
      () => handleSubscriptionSuccessRef.current?.(),
      () => process.env.REACT_APP_BASE_API_URL,
      sendSsoToken,
    );
    return () => {
      cleanup();
      unregisterIframeForLogout();
    };
  }, []);

  // Push the GBI API base URL down to the iframe as soon as it loads so it
  // talks to the backend matching this parent's environment.
  useEffect(() => {
    if (iframeLoaded && iframeRef.current) {
      sendApiBaseUrlToIframe(
        iframeRef.current,
        process.env.REACT_APP_BASE_API_URL,
        getTargetOrigin(),
      );
    }
  }, [iframeLoaded]);

  const handleIframeLoad = useCallback(() => {
    setIframeLoaded(true);
    registerIframeForLogout(iframeRef.current, getTargetOrigin());
    // Funnel: member reached the Insider+ iframe. Once per session — onLoad can
    // refire and the hook is mounted by several iframe pages.
    pushInsiderFunnelEventOnce('investor_hub_opened');
  }, []);

  const handleRetry = useCallback(async () => {
    if (isRetryingRef.current) return;
    isRetryingRef.current = true;
    setIsRetrying(true);
    retryCountRef.current = 0;
    // Explicit user retry — bypass the cache and mint a fresh token.
    clearCachedSsoToken();
    try {
      // Only close the modal if the fetch actually produced a new token.
      // A silent failure would otherwise hide the error UI without fixing
      // anything, leaving the user with a still-broken iframe.
      const success = await fetchSsoToken(true);
      if (success) {
        setAuthFailed(false);
      }
    } finally {
      isRetryingRef.current = false;
      setIsRetrying(false);
    }
  }, [fetchSsoToken]);

  // Re-navigating to the same parent route (e.g. clicking the header link
  // while already on a deep iframe page) keeps the parent URL unchanged, so
  // the iframe never reloads on its own. Bump iframeKey so React remounts
  // the <iframe>, re-derive src for pages that compute it dynamically, and
  // refetch the SSO token so the push-token effect re-arms for the
  // reloaded child. The explicit setIframeLoaded(false) is load-bearing:
  // remounting the DOM element doesn't reset React state on its own, and
  // without this the push-token effect would fire against the new (still
  // loading) iframe before its onLoad rearms iframeLoaded.
  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    if (skipNextLocationSyncRef?.current === location.pathname) {
      // eslint-disable-next-line no-param-reassign
      skipNextLocationSyncRef.current = null;
      return;
    }
    setIframeLoaded(false);
    setSsoToken(null);
    setIframeSrc(buildIframeSrc());
    setIframeKey((k) => k + 1);
    fetchSsoToken();
  }, [
    location.key,
    location.pathname,
    buildIframeSrc,
    fetchSsoToken,
    skipNextLocationSyncRef,
  ]);

  return {
    iframeRef,
    iframeSrc,
    iframeKey,
    iframeLoaded,
    isRecovering,
    isRetrying,
    authFailed,
    handleIframeLoad,
    handleRetry,
  };
};

export default useInsiderPlusIframe;
